import type { OperatorWorkBucket, OperatorWorkItem } from "../../../shared/types";
import { buildBrandReviewPath } from "./brand-navigation";
import { withQueryParams } from "./path-query";

export type InboxItemType = "approval" | "blocked";

export interface InboxItem {
  id: string;
  type: InboxItemType;
  bucket: OperatorWorkBucket;
  title: string;
  subtitle?: string;
  description: string;
  actionLabel: string;
  timestamp: string;
  briefId?: string | null;
  campaignId?: string | null;
  gateId?: string | null;
}

interface SelectInboxItemsArgs {
  brandId?: string;
  operatorWorkItems?: OperatorWorkItem[];
}

export function selectInboxItems({
  brandId,
  operatorWorkItems = [],
}: SelectInboxItemsArgs): InboxItem[] {
  if (!brandId) return [];

  const items: InboxItem[] = [];

  for (const workItem of operatorWorkItems) {
    const type: InboxItemType = workItem.bucket === "blocked" ? "blocked" : "approval";
    items.push({
      id: `${workItem.bucket}:${workItem.id}`,
      type,
      bucket: workItem.bucket,
      title: workItem.title,
      subtitle: workItem.channel ?? undefined,
      description: workItem.next_action,
      actionLabel: type === "approval" ? "Review" : "Resolve blocker",
      timestamp: workItem.updated_at,
      briefId: workItem.brief_id,
      campaignId: workItem.campaign_id,
      gateId: workItem.gate_id,
    });
  }

  return items.sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );
}

export function selectInboxBadgeCount(args: SelectInboxItemsArgs): number {
  return selectInboxItems(args).length;
}

/** Route an inbox item to its work-object destination. Shared by WorkspacePage
 *  and ShellSidePanel so the Home queue routes consistently (gate → campaign →
 *  workflows fallback). */
export function inboxItemRoute(item: InboxItem, brandId: string): string {
  if (item.gateId) return `/gates/${item.gateId}`;
  if (item.type === "approval") return item.briefId ? `${buildBrandReviewPath(brandId)}?briefId=${encodeURIComponent(item.briefId)}` : buildBrandReviewPath(brandId);
  if (item.campaignId) {
    const campaignPath = `/brand/${encodeURIComponent(brandId)}/campaigns/${encodeURIComponent(item.campaignId)}`;
    // A blocked campaign brief resolves on its own view, where Retry/Skip live;
    // the campaign page alone has no control that clears the blocker.
    return item.type === "blocked" && item.briefId
      ? withQueryParams(`${campaignPath}/briefs/${encodeURIComponent(item.briefId)}`, { document: "brief" })
      : campaignPath;
  }
  if (item.briefId) return buildBrandReviewPath(brandId);
  return "/workflows";
}
